import { useMemo } from 'react';
import { useUpgradesStore } from '../zustand/useUpgradesStore';
import { useGeneratorsStore } from '../zustand/useGeneratorsStore';
import { useCurrencyStore } from '../zustand/useCurrencyStore';
import { UpgradeStatus, UpgradeConfigKey, EntityUpgrades } from '../../types/game-types';
import { costToBuyNItems } from '../helpers/calculate-cost';

/**
 * Pure helper: checks if a generator's upgrades have at least one purchasable upgrade.
 * An upgrade is purchasable when:
 * - It's unlocked (status === UpgradeStatus.Unlocked)
 * - It hasn't reached max (purchased < purchaseLimit)
 * - The price is affordable (gold >= cost)
 */
export function hasPurchasableInGenerator(
  generatorUpgrades: EntityUpgrades,
  gold: number
): boolean {
  for (const upgrade of Object.values(generatorUpgrades)) {
    const isUnlocked =
      upgrade.status === UpgradeStatus.Unlocked && !upgrade.isLockedOnDemo;

    if (!isUnlocked) {
      continue;
    }

    const isMaxed = upgrade.purchaseLimit
      ? upgrade.purchased >= upgrade.purchaseLimit
      : false;

    if (isMaxed) {
      continue;
    }

    const nextPurchaseCost = costToBuyNItems(
      upgrade.cost.gold || 0,
      upgrade.costGrowthRate,
      upgrade.purchased,
      1
    );

    if (gold >= nextPurchaseCost) {
      return true;
    }
  }

  return false;
}

/**
 * Returns a map of generatorId -> whether that generator has at least one purchasable upgrade.
 * Pass generatorIds (e.g. tabListIds) so the map is keyed by the same IDs as the UI; each tab
 * then has a defined value and both the main button and per-tab indicators stay in sync.
 * Only counts as purchasable if the generator itself is purchased (at least 1), except for "click".
 */
export const useHasPurchasableUpgradesMap = (
  generatorIds: string[] = []
): Record<string, boolean> => {
  const upgrades = useUpgradesStore((store) => store.upgrades);
  const generators = useGeneratorsStore((store) => store.generators);
  const gold = useCurrencyStore((state) => state.gold);

  return useMemo(() => {
    const map: Record<string, boolean> = {};
    const ids = generatorIds ?? [];

    for (const generatorId of ids) {
      if (!generatorId || generatorId === 'BUG_HIDE') {
        map[generatorId] = false;
        continue;
      }

      const generatorUpgrades = upgrades[generatorId];
      if (!generatorUpgrades) {
        map[generatorId] = false;
        continue;
      }

      // Click has no generator to purchase; others require at least 1 purchased
      const isGeneratorOwned =
        generatorId === 'click' ||
        (generators[generatorId as keyof typeof generators]?.purchased ?? 0) > 0;
      map[generatorId] =
        isGeneratorOwned && hasPurchasableInGenerator(generatorUpgrades, gold);
    }

    return map;
  }, [upgrades, gold, generators]);
};

/**
 * Checks if a generator has at least one purchasable upgrade.
 * Prefer useHasPurchasableUpgradesMap when you need multiple generators or the main button indicator.
 * Only returns true if the generator itself is purchased (at least 1), except for "click".
 *
 * Memoized to only recalculate when gold, upgrades, or generators state changes.
 */
export const useHasPurchasableUpgrade = (generatorId: UpgradeConfigKey): boolean => {
  const upgrades = useUpgradesStore((store) => store.upgrades);
  const generators = useGeneratorsStore((store) => store.generators);
  const gold = useCurrencyStore((state) => state.gold);

  return useMemo(() => {
    // @ts-expect-error to fix bug where generatorId is not a valid UpgradeConfigKey
    if (!generatorId || generatorId === 'BUG_HIDE' || !upgrades[generatorId]) {
      return false;
    }

    const generatorUpgrades = upgrades[generatorId];
    const isGeneratorOwned =
      generatorId === 'click' ||
      (generators[generatorId as keyof typeof generators]?.purchased ?? 0) > 0;
    return isGeneratorOwned && hasPurchasableInGenerator(generatorUpgrades, gold);
  }, [generatorId, upgrades, gold, generators]);
};
